import { AfterViewInit, Component, ElementRef, HostListener, inject, Inject, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from "@angular/material/dialog";
import { OnGoingOrder } from "../../../../@domain/on-going-order";
import { PaymentMethods } from "../../../../@application/enum/payment-methods";
import { firstValueFrom, Observable } from "rxjs";
import { FormHelper, KeyValue } from '../../../../../../projects/vendors/src/lib/services/form-helper';
import { ApiBaseService } from "../../../../../../projects/vendors/src/lib/services/api-base.service";
import { StorageService } from "../../../../../../projects/vendors/src/lib/services/storage.service";
import { ApiEnum } from "../../../../@application/enum/api.enum";
import { FoodItemQuantity } from "../../../../@domain/food-item-quantity";
import { NgxPrintService, PrintOptions } from "ngx-print";
import { OrderType } from "../../../../@application/enum/order-type.enums";
import { RestaurantConfig } from "../../../../@domain/restaurant-config";
import { FoodItemPipe } from "../../../../../../projects/vendors/src/lib/pipe/food-item.pipe";
import { TableNamePipe } from "../../../../../../projects/vendors/src/lib/pipe/table-name.pipe";
import { RestaurantTable } from "../../../../@domain/restaurent-table";
import { PaymentTotals } from "../../../../../../projects/vendors/src/lib/components/bill-printer-section/payment-totals.interface";
import { FoodItem } from '../../../../@domain/food-item';

@Component({
    selector: 'lib-check-out-dialog-box',
    templateUrl: './check-out-dialog-box.component.html',
    styleUrls: ['./check-out-dialog-box.component.css'],
    standalone: false
})
export class CheckOutDialogBoxComponent implements OnInit, AfterViewInit, OnDestroy {

  @ViewChild('receiptWrapper') receiptWrapper!: ElementRef;
  @ViewChild('paidAmountInput') paidAmountInput!: ElementRef<HTMLInputElement>;

  discountAdded: boolean = false;
  paidAdded: boolean = false;
  serviceChargeChecked: boolean = true;
  taxChecked = true;
  isCard = false;
  cardTaxAdded: boolean = false;
  billForm!: FormGroup;
  totalAmount: number = 0;
  discount: number = 0;
  paidAmount: number = 0;
  grandTotal: number = 0;
  totalPrice: number = 0;
  balance: number = 0;
  serviceCharge: number = 0;
  tax: number = 0;
  cardTax: number = 0;
  paymentMethodKeyValue = [] as KeyValue[]
  placeholder = 'Enter remark';
  date = new Date();

  private apiBaseService = inject(ApiBaseService)
  private storageService = inject(StorageService)
  private printService = inject(NgxPrintService)
  private foodNamePipe = inject(FoodItemPipe)
  tableNamePipe = inject(TableNamePipe)
  private resizeObserver!: ResizeObserver;
  private isInternalPaidAmountUpdate = false;
  private submitting = false;
  private lastEnterPressTime = 0;
  private readonly DOUBLE_PRESS_THRESHOLD = 500; // milliseconds

  restaurantDetail = {} as RestaurantConfig
  paymentMethod = PaymentMethods
  orderType = OrderType
  foodItemQuantity: FoodItemQuantity[] = []
  userName!: string
  serviceChargeableTotalAmount: number = 0;
  // 2.5in receipt printer instead of the 3in default - see docs/RECEIPT_WIDTH.md
  smallTenant = 'b6778fd2-1972-4683-9998-3a9f3fb65fb0'
  tenantId!: string
  shouldApplyWidth = false;
  isBelow3In = false;

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: {
      onGoingOrder: OnGoingOrder,
      foods: FoodItem[],
      tables: RestaurantTable[];
    },
    private dialogRef: MatDialogRef<CheckOutDialogBoxComponent>) {
    this.paymentMethodKeyValue = FormHelper.PaymentMethodsList()
    this.totalPrice = this.data.onGoingOrder.cartResponseDetails.reduce((acc, curr) => acc + curr.totalPrice, 0)
  }

  async ngOnInit() {
    if (this.data.onGoingOrder.tables && this.data.onGoingOrder.tables.length === 1) {
      const table = this.data.tables.find(x => x.id === this.data.onGoingOrder.tables[0]);
      this.serviceChargeChecked = !!(table && table.hasServiceCharge);
    }
    this.tenantId = this.storageService.getFromSessionStorage('tenantId');
    this.shouldApplyWidth = this.tenantId === this.smallTenant;
    this.getFoodItemQuantity()
    this.formInitializer()
    this.restaurantDetail = await firstValueFrom(this.getRestaurantData())
    this.billForm.get('taxDetail.taxPercentage')?.setValue(this.restaurantDetail.restaurantDetail.tax)
    this.billForm.get('serviceChargeDetail.serviceChargePercentage')?.setValue(this.restaurantDetail.restaurantDetail.serviceCharge)

    // Auto-select cash payment
    this.billForm.get('paymentMethod')?.setValue(1);

    this.calculateTotalAmount();
    this.updateCalculations();
    this.billForm.get('discountDetail.discountPercentage')?.valueChanges.subscribe(() => this.updateCalculations());
    this.billForm.get('paidAmount')?.valueChanges.subscribe(() => {
      // Clear transient input if the change came from external source
      if (!this.isInternalPaidAmountUpdate) {
        this.transientRawInput = null;
      }
      this.updateCalculations();
    });
    this.billForm.get('paymentMethod')?.valueChanges
      .subscribe({
        next: (value) => {
          if (value === 1 || value === 3) {
            this.placeholder = 'Enter remark';
            this.isCard = false;
            this.removeCardTax();
          } else if (value === 2) {
            this.placeholder = 'Enter the last 4 digits of the card';
            this.isCard = true;
          } else if (value === 4) {
            this.placeholder = 'Enter payment method';
            this.isCard = false;
            this.removeCardTax();
          }
        }
      })

    const discountDetails: DiscountDetails[] = this.storageService.getFromLocalStorage('discountDetails') || [];
    const discountDetail = discountDetails.find(discount => discount.orderId === this.data.onGoingOrder.orderId)
    if (discountDetail) {
      this.billForm.get('discountDetail.discountPercentage')?.setValue(discountDetail.discountPercentage)
      this.discountAdded = true
    }
  }

  formInitializer() {
    this.userName = this.storageService.getFromSessionStorage('userDetails')?.givenName
    this.billForm = new FormGroup({
      orderId: new FormControl(this.data.onGoingOrder.orderId),
      paymentMethod: new FormControl(null, [Validators.required]),
      totalOrderAmount: new FormControl(this.totalPrice, [Validators.required]),
      paidAmount: new FormControl(null, [Validators.required, Validators.min(0)]),
      balance: new FormControl(null),
      cardTax: new FormControl(null),
      netAmount: new FormControl(null, [Validators.min(0)]),
      remarks: new FormControl(null),
      marginPercentage: new FormControl(this.data.onGoingOrder.marginPercentage),
      discountDetail: new FormGroup({
        discountPercentage: new FormControl(null, [Validators.min(0),
        Validators.max(this.data.onGoingOrder.marginPercentage)]),
        discountAmount: new FormControl(null, [Validators.min(0)]),
        amountBeforeDiscount: new FormControl(this.totalPrice),
        amountAfterDiscount: new FormControl(null),
      }),
      taxDetail: new FormGroup({
        taxPercentage: new FormControl(null),
        taxAmount: new FormControl(null),
        amountBeforeTax: new FormControl(this.totalPrice),
        amountAfterTax: new FormControl(null),
      }),
      serviceChargeDetail: new FormGroup({
        serviceChargePercentage: new FormControl(null),
        serviceChargeAmount: new FormControl(null),
        amountBeforeServiceCharge: new FormControl(this.totalPrice),
        amountAfterServiceCharge: new FormControl(null),
      }),
    });
  }


  ngAfterViewInit(): void {
    // Set up resize observer for receipt wrapper
    if (this.receiptWrapper?.nativeElement) {
      this.resizeObserver = new ResizeObserver(entries => {
        for (let entry of entries) {
          const widthPx = entry.contentRect.width;
          this.isBelow3In = widthPx < 250; // 3in = 288px at 96dpi
        }
      });

      this.resizeObserver.observe(this.receiptWrapper.nativeElement);
    }

    // Auto-focus on paid amount input
    setTimeout(() => {
      if (this.paidAmountInput?.nativeElement) {
        this.paidAmountInput.nativeElement.focus();
      }
    }, 100);
  }

  get paymentTotals(): PaymentTotals {
    return {
      discountAmount: this.discountAdded ? this.discount : undefined,
      serviceChargeAmount: this.serviceChargeChecked ? this.serviceCharge : undefined,
      serviceChargePercentage: this.serviceChargeChecked ? this.billForm.get('serviceChargeDetail.serviceChargePercentage')?.value : undefined,
      taxAmount: this.taxChecked ? this.tax : undefined,
      taxPercentage: this.taxChecked ? this.billForm.get('taxDetail.taxPercentage')?.value : undefined,
      paymentMethodValue: this.billForm.get('paymentMethod')?.value,
      paidAmount: this.billForm.get('paidAmount')?.value,
      grandTotal: this.grandTotal,
      balance: this.balance
    };
  }

  ngOnDestroy(): void {
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
    }
  }

  private removeCardTax() {
    if (this.cardTaxAdded) {
      this.billForm.get('cardTax')?.setValue(0);
      this.cardTaxAdded = false;
      this.updateCalculations();
    }
  }

  tableName(tables: string[]): string {
    if (tables.length == 0) {
      return 'POS'
    } else if (tables.length == 1) {
      return this.data.tables.find(x => x.id == tables[0])?.name!
    }
    return tables.map(x => this.tableNamePipe.transform(x, this.data.tables)).join(', ')
  }


  isNameContainsSpecialCharacter(name: string): boolean {
    const foodName = this.foodNamePipe.transform(name, this.data.foods);
    const bracketsRegex = /[()]/;
    return bracketsRegex.test(foodName);
  }


  getRestaurantData(): Observable<RestaurantConfig> {
    return this.apiBaseService.GET<RestaurantConfig>([ApiEnum.RestaurantModule, ApiEnum.SystemSetting, ApiEnum.RestaurantDetail])
  }


  calculateTotalAmount() {
    this.totalAmount = this.data.onGoingOrder.cartResponseDetails.reduce((acc, curr) => acc + curr.totalPrice, 0)
    this.serviceChargeableTotalAmount = this.data.onGoingOrder.cartResponseDetails
      .filter(x => x.isServiceCharge || x.isServiceCharge == null).reduce((acc, curr) => acc + curr.totalPrice, 0)
  }

  private calculateGrandTotal() {
    const discount = this.billForm.get('discountDetail.discountPercentage')?.value || 0;
    const cardTax = this.billForm.get('cardTax')?.value || 0;
    this.discount = (this.totalAmount * discount) / 100;
    this.cardTax = (this.totalAmount * cardTax) / 100;
    let serviceChargeAmount = 0;
    let taxAmount = 0;
    if (this.serviceChargeChecked) {
      serviceChargeAmount = this.serviceChargeChecked ? (this.serviceChargeableTotalAmount * this.billForm.get('serviceChargeDetail.serviceChargePercentage')?.value) / 100 : 0;
      this.billForm.get('serviceChargeDetail.serviceChargeAmount')?.setValue(serviceChargeAmount);
      this.billForm.get('serviceChargeDetail.amountAfterServiceCharge')?.setValue(this.totalAmount + serviceChargeAmount);
    }
    if (this.taxChecked) {
      taxAmount = (this.totalAmount * this.billForm.get('taxDetail.taxPercentage')?.value) / 100;
      this.billForm.get('taxDetail.taxAmount')?.setValue(taxAmount);
      this.billForm.get('taxDetail.amountAfterTax')?.setValue(this.totalAmount + taxAmount);
    }
    if (this.discountAdded) {
      this.billForm.get('discountDetail.discountAmount')?.setValue(this.discount);
      this.billForm.get('discountDetail.amountAfterDiscount')?.setValue(this.totalAmount - this.discount);
    }
    this.grandTotal = this.totalAmount - this.discount + serviceChargeAmount + taxAmount + this.cardTax;
    this.billForm.get('netAmount')?.setValue(this.grandTotal);
    this.serviceCharge = serviceChargeAmount;
    this.tax = taxAmount;
  }

  updateCalculations() {
    this.calculateGrandTotal();
    const paidAmount = this.billForm.get('paidAmount')?.value || 0;
    this.balance = paidAmount - this.grandTotal;
    if (paidAmount > 0) {
      this.billForm.get('balance')?.setValue(+parseFloat(String(this.balance)).toFixed(2));
    }
  }

  onCardTax() {
    this.cardTaxAdded = this.billForm.get('cardTax')?.value > 0;
    this.updateCalculations();
  }

  onDiscountAdded() {
    this.discountAdded = this.billForm.get('discountDetail.discountPercentage')?.value > 0;
    if (this.billForm.get('discountDetail.discountPercentage')?.value <= this.data.onGoingOrder.marginPercentage) {
      this.updateCalculations();
    }
    const discountDetail: DiscountDetails = {
      orderId: this.data.onGoingOrder.orderId,
      discountPercentage: this.billForm.get('discountDetail.discountPercentage')?.value
    }

    const discountDetails: DiscountDetails[] = this.storageService.getFromLocalStorage('discountDetails') || [];
    const savedDiscountDetail = discountDetails.find(discount => discount.orderId === this.data.onGoingOrder.orderId)
    if (savedDiscountDetail) {
      discountDetails.splice(discountDetails.indexOf(savedDiscountDetail), 1)
    }
    discountDetails.push(discountDetail)
    this.storageService.saveToLocalStorage('discountDetails', discountDetails)

  }

  onPaidAmount() {
    this.paidAdded = true;
    this.updateCalculations();
  }

  onCloseOrder() {
    if (this.billForm.valid) {
      this.submitForm()
    } else {
      this.markFormGroupTouched(this.billForm);
    }
  }

  private markFormGroupTouched(formGroup: FormGroup): void {
    Object.keys(formGroup.controls).forEach(key => {
      const control = formGroup.get(key);
      if (control instanceof FormGroup) {
        this.markFormGroupTouched(control);
      } else {
        control?.markAsTouched();
        control?.markAsDirty();
        control?.updateValueAndValidity({ onlySelf: true });
      }
    });
  }

  private submitForm() {
    // Close Order is clickable again while the POST is in flight, so guard the double submit.
    if (this.submitting) {
      return;
    }
    // Read the value being sent, not the discountAdded flag - the flag goes stale when the
    // field is cleared and a null percentage fails to deserialise as a decimal server-side.
    if (!this.billForm.get('discountDetail.discountPercentage')?.value) {
      this.billForm.get('discountDetail.discountPercentage')?.setValue(0)
      this.billForm.get('discountDetail.discountAmount')?.setValue(0)
      this.billForm.get('discountDetail.amountAfterDiscount')?.setValue(0)
    }
    if (!this.serviceChargeChecked) {
      this.billForm.get('serviceChargeDetail.serviceChargePercentage')?.setValue(0)
      this.billForm.get('serviceChargeDetail.amountAfterServiceCharge')?.setValue(0)
      this.billForm.get('serviceChargeDetail.serviceChargeAmount')?.setValue(0)
    }
    if (!this.taxChecked) {
      this.billForm.get('taxDetail.taxPercentage')?.setValue(0)
      this.billForm.get('taxDetail.amountAfterTax')?.setValue(0)
      this.billForm.get('taxDetail.taxAmount')?.setValue(0)
    }
    // this.printBill()
    this.submitting = true;
    this.apiBaseService.POST_API([ApiEnum.RestaurantModule, ApiEnum.OrderBill], this.billForm.value,
      true, true, 'Order has been closed successfully')
      .subscribe({
        next: (res) => {
          this.dialogRef.close(true)
        },
        error: () => {
          // Error alert comes from the interceptor; just let the cashier retry.
          this.submitting = false;
        }
      })
  }

  private printBill() {
    const customPrintOptions: PrintOptions = new PrintOptions({
      printSectionId: 'print-section',
      useExistingCss: true,
      // Add any other print options as needed
    });
    this.printService.print(customPrintOptions)
  }

  private getFoodItemQuantity(): void {
    this.apiBaseService.GET<FoodItemQuantity[]>([ApiEnum.FoodModule, ApiEnum.FoodItemQuantityTag])
      .subscribe(res => {
        this.foodItemQuantity = res
      });
  }

  selectPaymentMethod(method: number): void {
    this.billForm.get('paymentMethod')?.setValue(method);
  }

  @HostListener('document:keydown', ['$event'])
  handleKeyboardNavigation(event: KeyboardEvent): void {
    // Handle double Enter key press to open print view
    if (event.key === 'Enter') {
      const currentTime = Date.now();
      if (currentTime - this.lastEnterPressTime < this.DOUBLE_PRESS_THRESHOLD) {
        event.preventDefault();
        this.openPrintView();
        this.lastEnterPressTime = 0; // Reset to avoid triple-press triggering again
        return;
      }
      this.lastEnterPressTime = currentTime;
      return;
    }

    // Only handle Shift + Arrow keys
    if (!event.shiftKey || (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight')) {
      return;
    }

    // Don't interfere when user is typing in text areas or inputs (except paidAmount)
    const target = event.target as HTMLElement;
    if (target.tagName === 'TEXTAREA' ||
        (target.tagName === 'INPUT' && target !== this.paidAmountInput?.nativeElement)) {
      return;
    }

    event.preventDefault();

    const currentMethod = this.billForm.get('paymentMethod')?.value || 1;
    let newMethod: number;

    if (event.key === 'ArrowRight') {
      // Move to next payment method (1-6, wrap around)
      newMethod = currentMethod >= 6 ? 1 : currentMethod + 1;
    } else {
      // Move to previous payment method (1-6, wrap around)
      newMethod = currentMethod <= 1 ? 6 : currentMethod - 1;
    }

    this.selectPaymentMethod(newMethod);
  }

  openPrintView(): void {
    const customPrintOptions: PrintOptions = new PrintOptions({
      printSectionId: 'print-section',
      useExistingCss: true,
    });
    this.printService.print(customPrintOptions);
  }

  // Transient raw string used during manual input to preserve trailing decimals
  // This gets cleared on external form value changes to sync with programmatic updates
  private transientRawInput: string | null = null;

  // Helper to get the raw input string representation
  private getRawPaidAmountString(): string {
    // Use transient input if available (user is actively typing)
    if (this.transientRawInput !== null) {
      return this.transientRawInput;
    }
    // Otherwise derive from form control value
    const value = this.billForm.get('paidAmount')?.value;
    if (value === null || value === undefined || value === '') {
      return '';
    }
    return String(value);
  }

  get paidAmountHasValue(): boolean {
    const value = this.billForm.get('paidAmount')?.value;
    return value !== null && value !== undefined && value !== '';
  }

  get formattedPaidAmount(): string {
    const value = this.billForm.get('paidAmount')?.value;
    if (value === null || value === undefined || value === '') {
      return '';
    }
    // Format with thousand separators
    const parts = String(value).split('.');
    parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    return parts.join('.');
  }

  onPaidAmountKeydown(_event: KeyboardEvent): void {
    // Intentionally left as a no-op: Tab key handling was removed because payment method is now selected via dropdown.
    return;
  }

  onNumpadClick(value: string): void {
    // Get current string representation
    const currentString = this.getRawPaidAmountString();
    
    // Prevent multiple decimal points
    if (value === '.' && currentString.includes('.')) {
      return;
    }
    
    // Build the new string
    let newString: string;
    if (value === '.' && currentString === '') {
      newString = '0.';
    } else {
      newString = currentString + value;
    }
    
    // Store transient raw input to preserve trailing decimals during typing
    this.transientRawInput = newString;
    
    // Update form control with numeric value
    const numericValue = parseFloat(newString);
    if (!isNaN(numericValue)) {
      this.isInternalPaidAmountUpdate = true;
      try {
        this.billForm.get('paidAmount')?.setValue(numericValue);
        this.onPaidAmount();
      } finally {
        this.isInternalPaidAmountUpdate = false;
      }
    }
  }

  onNumpadDelete(): void {
    // Get current string representation
    const currentString = this.getRawPaidAmountString();
    
    if (currentString.length > 0) {
      // Remove last character
      const newString = currentString.slice(0, -1);
      
      this.isInternalPaidAmountUpdate = true;
      try {
        if (newString === '' || newString === '0') {
          this.transientRawInput = null;
          this.billForm.get('paidAmount')?.setValue(null);
          this.paidAdded = false;
        } else {
          // Store transient raw input to preserve trailing decimals
          this.transientRawInput = newString;
          const numericValue = parseFloat(newString);
          this.billForm.get('paidAmount')?.setValue(numericValue);
        }
        this.updateCalculations();
      } finally {
        this.isInternalPaidAmountUpdate = false;
      }
    }
  }

  onKeyboardInput(event: Event): void {
    const input = event.target as HTMLInputElement;
    // Strip commas and non-numeric characters (except decimal point)
    let rawValue = input.value.replace(/,/g, '').replace(/[^0-9.]/g, '');

    // Ensure only one decimal point
    const decimalCount = (rawValue.match(/\./g) || []).length;
    if (decimalCount > 1) {
      const firstDecimalIndex = rawValue.indexOf('.');
      rawValue = rawValue.substring(0, firstDecimalIndex + 1) + rawValue.substring(firstDecimalIndex + 1).replace(/\./g, '');
    }

    this.isInternalPaidAmountUpdate = true;
    try {
      if (rawValue === '' || rawValue === null) {
        this.transientRawInput = null;
        this.billForm.get('paidAmount')?.setValue(null);
        this.paidAdded = false;
      } else {
        // Store transient raw input to preserve trailing decimals during typing
        this.transientRawInput = rawValue;
        const numericValue = parseFloat(rawValue);
        if (!isNaN(numericValue) && numericValue >= 0) {
          this.billForm.get('paidAmount')?.setValue(numericValue);
          this.onPaidAmount();
        }
      }
      this.updateCalculations();
    } finally {
      this.isInternalPaidAmountUpdate = false;
    }
  }

  onPaidAmountKeyPress(event: KeyboardEvent): boolean {
    const charCode = event.key;
    // Allow: digits, decimal point, backspace, delete, tab, escape, enter, arrows
    if (
      /^[0-9]$/.test(charCode) ||
      charCode === '.' ||
      charCode === 'Backspace' ||
      charCode === 'Delete' ||
      charCode === 'Tab' ||
      charCode === 'Escape' ||
      charCode === 'Enter' ||
      charCode === 'ArrowLeft' ||
      charCode === 'ArrowRight'
    ) {
      // Only allow one decimal point
      const currentString = this.getRawPaidAmountString();
      if (charCode === '.' && currentString.includes('.')) {
        event.preventDefault();
        return false;
      }
      return true;
    }
    event.preventDefault();
    return false;
  }
}

export interface DiscountDetails {
  orderId: string;
  discountPercentage: number;
}
